/**
 * The floor-plan company logo, recorded in the configuration through `useGatedUploadScope`. The stored
 * value is the ORIGINAL filename (wire-compatible with legacy `setCompanyLogo(file.name)`), not the
 * gcFilename. The logo does not gate completeness.
 */

import type { CartItemId } from 'models/purchaseFlowRevamp'

import { usePfStore } from '../../store'
import { useFloorPlanLogoUpload, useGatedUploadScope } from '../../fileHub'
import { useFloorPlanConfigDraft } from './useFloorPlanConfigDraft'

export function useFloorPlanLogoSync(cartItemId: CartItemId) {
  const { value, patch } = useFloorPlanConfigDraft(cartItemId)
  const logo = useFloorPlanLogoUpload()

  // The cart item's catalogue product code — the v2 mint takes it verbatim. `null`
  // before a variant is chosen → the upload rejects cleanly (chip → FileState.ERROR).
  const variantId = usePfStore((s) => s.items.find((i) => i.id === cartItemId)?.variantId ?? null)

  // The configuration keeps the logo's ORIGINAL name, but a restored file is addressed by its stored name:
  // seeding the original name polled the thumbnail for a file that does not exist. A logo saved without
  // its stored name is not seeded at all.
  const seedStored = () => {
    if (value.companyLogoStoredFilename) logo.seed([value.companyLogoStoredFilename])
  }
  const storedNameOf = (originalName: string | undefined) =>
    logo.entries.filter((entry) => entry.originalFilename === originalName && entry.gcFilename).at(-1)?.gcFilename ?? null

  const gate = useGatedUploadScope(
    { ...logo, seed: seedStored },
    logo.companyLogoFilename ? [logo.companyLogoFilename] : [],
    value.companyLogo ? [value.companyLogo] : [],
    // A logo turns the logo section on; clearing it leaves the client's toggle as it was.
    ([filename]) => patch({
      companyLogo: filename ?? null,
      companyLogoStoredFilename: storedNameOf(filename),
      isActiveLogo: filename ? true : value.isActiveLogo,
    })
  )

  const uploadLogo = (files: File[]) => gate.upload(files, { productCode: variantId })
  const deleteLogo = (id: string) => {
    gate.remove(id)
    patch({ companyLogo: null, companyLogoStoredFilename: null })
  }

  // "Create from scratch" empties the upload area as well as the configuration: a logo still shown there
  // would otherwise be written straight back, and the from-scratch order would ship it.
  const clearLogo = () => {
    logo.entries.forEach((e) => gate.remove(e.id))
    patch({ companyLogo: null, companyLogoStoredFilename: null, isActiveLogo: false })
  }

  return {
    logo,
    hasRestoredLogo: gate.hadRestored,
    uploadLogo,
    deleteLogo,
    clearLogo,
  }
}
